import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { AWSLambda } from '../../AWSLambda.service';

export interface AddInvoiceDialogData {
}

@Component({
  selector: 'app-add-invoice-dialog',
  templateUrl: './add-invoice-dialog.component.html',
  styleUrls: ['./add-invoice-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AddInvoiceDialogComponent {
  form: FormGroup;
  fileName = '';
  fileBase64 = '';
  minEndDate: Date | null = null;
  isUploading = false;
  
  endDateFilter = (date: Date | null): boolean => {
    if (!date || !this.minEndDate) {
      return true;
    }
    return date >= this.minEndDate;
  };

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddInvoiceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddInvoiceDialogData,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private lambda: AWSLambda
  ) {
            this.form = this.fb.group({
          amount: ['', [Validators.required, this.numericValidator, this.minAmountValidator]],
          startDate: [null, Validators.required],
          endDate: [null, Validators.required],
          file: [null, Validators.required]
        }, { validators: this.dateRangeValidator });
  }

  private numericValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value || control.value === '') return null;
    
    const stringValue = String(control.value).trim();
    if (stringValue === '') return null;
    
    if (stringValue.endsWith('.') || stringValue.endsWith(',')) {
      const beforeDecimal = stringValue.slice(0, -1);
      if (beforeDecimal && !isNaN(parseFloat(beforeDecimal))) {
        return null;
      }
    }
    
    const normalizedValue = stringValue.replace(',', '.');
    const numericValue = parseFloat(normalizedValue);
    
    if (isNaN(numericValue)) {
      return { numeric: true };
    }
    
    return null;
  }

  private minAmountValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value || control.value === '') return null;
    
    const stringValue = String(control.value).trim();
    if (stringValue === '' || stringValue.endsWith('.') || stringValue.endsWith(',')) {
      return null;
    }
    
    const normalizedValue = stringValue.replace(',', '.');
    const numericValue = parseFloat(normalizedValue);
    
    if (!isNaN(numericValue) && numericValue <= 0) {
      return { min: true };
    }
    
    return null;
  }

  private dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const startDate = control.get('startDate')?.value;
    const endDate = control.get('endDate')?.value;
    
    if (startDate && endDate && startDate > endDate) {
      return { dateRangeInvalid: true };
    }
    
    return null;
  }

  onStartDateChange(event: MatDatepickerInputEvent<Date>) {
    const startDate = event.value;
    this.form.get('startDate')?.setValue(startDate);
    
    if (startDate) {
      this.minEndDate = new Date(startDate);
      
      const currentEndDate = this.form.get('endDate')?.value;
      if (currentEndDate && currentEndDate < startDate) {
        this.form.get('endDate')?.setValue(null);
      }
    } else {
      this.minEndDate = null;
    }
  }

  onEndDateChange(event: MatDatepickerInputEvent<Date>) {
    const endDate = event.value;
    this.form.get('endDate')?.setValue(endDate);
  }

  onAmountChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    this.form.get('amount')?.setValue(value);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    
    if (!file) {
      this.fileName = '';
      this.fileBase64 = '';
      this.form.get('file')?.setValue(null);
      return;
    }
    
    if (file.type !== 'application/pdf') {
      this.form.get('file')?.setErrors({ type: true });
      this.fileName = '';
      this.fileBase64 = '';
      return;
    }
    
    this.fileName = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] || '';
      this.fileBase64 = base64;
      this.form.get('file')?.setValue('loaded');
      this.form.get('file')?.setErrors(null);
    };
    reader.readAsDataURL(file);
  }

  async submit() {
    if (this.form.invalid || !this.fileBase64) {
      this.form.markAllAsTouched();
      return;
    }
    
    const startDate = this.form.value.startDate;
    const endDate = this.form.value.endDate;
    
    if (startDate > endDate) {
      this.form.setErrors({ dateRangeInvalid: true });
      return;
    }
    
    const amountString = String(this.form.value.amount).replace(',', '.');
    const amount = parseFloat(amountString);
    
    this.isUploading = true;
    
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: {
          method: 'uploadInvoice',
          amount: amount,
          startDate: this.formatDate(startDate, false),
          endDate: this.formatDate(endDate, true),
          filename: this.fileName
        }
      });

      console.log('>> response:', response);

      if (!response || !response.url || !response.key) {
        throw new Error('Invalid response from uploadInvoice');
      }

      let base64Data: string = this.fileBase64 || '';
      const commaIndex = base64Data.indexOf(',');
      if (commaIndex !== -1) {
        base64Data = base64Data.substring(commaIndex + 1);
      }
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const fileBlob = new Blob([byteArray], { type: 'application/pdf' });

      const putRes = await fetch(response.url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/pdf' },
        body: fileBlob
      });

      console.log('>> putRes:', putRes);

      if (!putRes.ok) {
        throw new Error('Failed to upload file to S3');
      }

      this.snackBar.open(
        this.translate.instant('BTB.DASHBOARD.INVOICE_UPLOADED_SUCCESS'),
        'Cerrar',
        {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['success-snackbar']
        }
      );

      this.dialogRef.close({ success: true });
    } catch (error) {
      console.error('Error uploading invoice:', error);
      this.snackBar.open(
        this.translate.instant('BTB.DASHBOARD.INVOICE_UPLOAD_ERROR'),
        'Cerrar',
        {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        }
      );
    } finally {
      this.isUploading = false;
    }
  }

  private formatDate(date: Date, isEndDate: boolean = false): string {
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const time = isEndDate ? '23:59:59' : '00:00:00';
    
    const dateString = `${year}-${month}-${day} ${time}`;
    const utcDate = new Date(dateString + ' UTC');
    
    return utcDate.toISOString().replace('T', ' ').replace('Z', '');
  }

  cancel() {
    if (!this.isUploading) {
      this.dialogRef.close();
    }
  }

  get isFormValid(): boolean {
    return this.form.valid && this.fileBase64.length > 0 && !this.isUploading;
  }
}
